import React, { useState } from "react";
import {
  TextField,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  CircularProgress,
  InputAdornment,
  IconButton,
  Box,
  Alert,
} from "@mui/material";
import ClearIcon from "@mui/icons-material/Clear";
import { getClasificacionesGrupoTemporada } from "../services/clasificacionService";
import { getJornadasByIdGrupoAndTemporada } from "../services/jornadasService";
import { useNavigate } from "react-router-dom";
import Filters from "./Filters";
import { useAppContext } from "../context/AppContext";
import { findMatchday } from "../utils/matchdays";

export const getTeamsFromMatchday = (data) => {
  const matchdays = Array.isArray(data)
    ? data
    : data?.jornadas ?? data?.data ?? [data];
  const teams = new Map();

  matchdays
    .flatMap((matchday) => matchday?.partidos ?? [])
    .forEach((match) => {
      [
        {
          codequipo:
            match.codEquipoLocal ??
            match.idEquipoLocal ??
            match.equipoLocalId,
          nombre: match.equipoLocal,
        },
        {
          codequipo:
            match.codEquipoVisitante ??
            match.idEquipoVisitante ??
            match.equipoVisitanteId,
          nombre: match.equipoVisitante,
        },
      ].forEach((team) => {
        if (team.codequipo != null && team.nombre) {
          teams.set(String(team.codequipo), {
            ...team,
            codequipo: String(team.codequipo),
          });
        }
      });
    });

  return [...teams.values()];
};

const BuscarEquipo = ({ onEquipoSeleccionado }) => {
  const [nombre, setNombre] = useState("");
  const [, setEquipos] = useState([]);
  const [datosEquipos, setDatosEquipos] = useState([]);
  const [, setEquipoSeleccionado] = useState(null);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState(false);
  const { filters } = useAppContext();
  const navigate = useNavigate(); // Crear la instancia de navigate

  // 🏆 Seleccionar un jugador y hacer otra consulta
  const handleSeleccionEquipo = (equipo) => {
    setNombre(equipo.nombre);
    setEquipos([]);
    setDatosEquipos([]);
    setEquipoSeleccionado(equipo);
    // Llamar a la función de la prop para hacer otra consulta
    const idEquipo = equipo.codequipo;
    const idGrupo = filters.grupo;
    const idTemporada = filters.temporada;
    const idCompeticion = filters.competicion;
    onEquipoSeleccionado(
      idTemporada,
      idCompeticion,
      idGrupo,
      idEquipo,
      equipo,
      filters.federacion,
      filters.temporadaNombre
    );
  };

  const handleApplyFilters = async () => {
    if (!filters.temporada || !filters.grupo) return;

    setLoading(true);
    setLoadError(false);
    try {
      const classifications = await getClasificacionesGrupoTemporada(
        filters.temporada,
        filters.grupo,
        navigate
      );
      let teams = classifications?.[0]?.clasificacion ?? [];

      if (teams.length === 0) {
        const matchdays = await getJornadasByIdGrupoAndTemporada(
          filters.grupo,
          filters.temporada,
          navigate
        );
        const firstMatchday = findMatchday(matchdays, 1);
        teams = getTeamsFromMatchday(firstMatchday);
      }

      setDatosEquipos(teams);
    } catch {
      setDatosEquipos([]);
      setLoadError(true);
    } finally {
      setLoading(false);
    }
  };
  const normalizedName = nombre.trim().toLocaleLowerCase("es");
  const visibleTeams = datosEquipos.filter((equipo) =>
    equipo.nombre.toLocaleLowerCase("es").includes(normalizedName)
  );

  return (
    <div>
      <Box>
        <Filters onApplyFilters={handleApplyFilters} autoApplySingleGroup />
      </Box>
      <TextField
        label="Buscar equipo"
        variant="outlined"
        fullWidth
        value={nombre}
        onChange={(e) => setNombre(e.target.value)}
        InputProps={{
          endAdornment: nombre && (
            <InputAdornment position="end">
              <IconButton onClick={() => setNombre("")} edge="end">
                <ClearIcon />
              </IconButton>
            </InputAdornment>
          ),
        }}
      />

      {loading && <CircularProgress size={24} style={{ marginTop: 10 }} />}
      {loadError && (
        <Alert severity="error" sx={{ mt: 2 }}>
          No se pudieron cargar los equipos del grupo.
        </Alert>
      )}

      {!loading && visibleTeams.length > 0 && (
        <List
          aria-label="Equipos disponibles"
          sx={{
            mt: 1.5,
            py: 0.5,
            border: 1,
            borderColor: "divider",
            borderRadius: 2,
            bgcolor: "background.paper",
            overflow: "hidden",
          }}
        >
          {visibleTeams
            ?.slice() // Hacer una copia para no modificar el estado original
            .sort((a, b) => a.nombre.localeCompare(b.nombre)) // Ordenar por nombre
            .map((equipo) => (
              <ListItem
                key={equipo?.codequipo}
                disablePadding
              >
                <ListItemButton
                  onClick={() => handleSeleccionEquipo(equipo)}
                  aria-label={`Seleccionar ${equipo.nombre}`}
                  sx={{
                    minHeight: 48,
                    px: 2,
                    color: "text.primary",
                    borderBottom: 1,
                    borderColor: "divider",
                    transition: "background-color 180ms ease, color 180ms ease",
                    "&:hover": {
                      bgcolor: "action.hover",
                      color: "text.primary",
                    },
                    "&:focus-visible": {
                      bgcolor: "action.selected",
                      outline: (theme) => `3px solid ${theme.palette.primary.main}`,
                      outlineOffset: -3,
                    },
                    "&:active": {
                      bgcolor: "action.selected",
                    },
                  }}
                >
                  <ListItemText
                    primary={equipo.nombre}
                    primaryTypographyProps={{ fontWeight: 600 }}
                  />
                </ListItemButton>
              </ListItem>
            ))}
        </List>
      )}
      {!loading && datosEquipos.length > 0 && visibleTeams.length === 0 && (
        <Alert severity="info" sx={{ mt: 2 }}>No hay equipos que coincidan con la búsqueda.</Alert>
      )}
    </div>
  );
};

export default BuscarEquipo;
